import { Component, inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { GenericDataType } from 'src/modules/enaio/shared/GenericDataType';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { AdvancedForm } from '../../models/advanced-form.model';
import { GenDocDto } from '../../models/gen-doc.dto';
import { DataSourceHandler } from '../../models/data-source-handler.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FileUploadService } from 'src/modules/sm-base/services/file-upload-service';
import { GenDoc } from '../../models/gen-doc.model';

@Component({
  selector: 'app-invoice-generator-main',
  templateUrl: './invoice-generator-main.component.html',
  styleUrl: './invoice-generator-main.component.scss'
})
export class InvoiceGeneratorMainComponent extends ComponentView {

    fileUpload = inject(FileUploadService);
    
    formId = "invoice";
    form: AdvancedForm;
    docs: GenDocDto[];
    templates: GenDocDto[];
    table: TableData;
    showOnlyOwn = true;
    dataSources: DataSourceHandler;
    locations: FrontendFieldListItem[];
    selectedLocations: string[] = [];
    newInvoiceMenuItems: MenuItem[] = [];
    importMenuItems: MenuItem[] = [];
    importedItems: {title: string, table: TableData}[] = [];

    async initParams(): Promise<boolean> {
        this.app.updateNavigation("Rechnungs-Übersicht", [{label: "Rechnungs-Übersicht", url: "/invoice-generator/home"}]);

        this.form = await RestEndpoint.main().query({id: this.formId}).run("api/ivg/getForm").get(AdvancedForm);
        this.docs = await RestEndpoint.main().query({includeTableColumns: true}).run("api/ivg/doc").list(GenDocDto);    
        this.templates = await RestEndpoint.main().query({templates: true}).run("api/ivg/doc").list(GenDocDto); 

        let allowedLocations = Utils.arrayGetUnique(Utils.arrayExplode(this.app.auth.userDetails?.getUserGroups(), ug => Utils.split(ug.getAttributeValue("locations") ?? "", ",")));
                    
        this.dataSources = new DataSourceHandler(this.form.dataSources);
        let dataSourceLocations = await this.dataSources.getOrFetch("locations");
        dataSourceLocations.data = dataSourceLocations.data.filter(dsi => allowedLocations.includes(dsi.data.Location) || allowedLocations.includes(dsi.data.Location + ':' + dsi.data.Number));
                
        this.locations = FrontendFieldListItem.make(dataSourceLocations.data, item => [item.key, item.label, item]);
        let ls = Utils.stringDef(localStorage.getItem("selectedLocations"), "[]");
        try {
            this.selectedLocations = Utils.fromJson(ls);
            if (this.selectedLocations.length == 0) {
                this.selectedLocations = dataSourceLocations.data.map(item => item.key);
            }
        }
        catch {
            this.selectedLocations = dataSourceLocations.data.map(item => item.key);
        }

        this.rowSelected(null);
        this.refillTable();

        this.importMenuItems = Utils.arrayWithoutNull([
            {
                label: "Tankrechnungen",
                command: () => this.importDocuments("Tankrechnungen")
            }
        ]);

        return Promise.resolve(true);
    }
    
    async importDocuments(jobId: string): Promise<void> {
        let file = Utils.arrayGetSafe(await this.fileUpload.chooseFile(true, ["xlsx"]), 0);
        if (file != null) {
            let docs = await RestEndpoint.main().query({jobId}).body(Utils.bytesToBase64(file.contents)).post().run("api/ivg/import").list(GenDocDto);  
            this.importedItems = [...this.importedItems, { title: jobId, table: this.createTableData(docs, false)}];  
        }

    }
    
    rowSelected(event: any): void {
        let doc = event?.raw as GenDocDto;
        this.newInvoiceMenuItems = Utils.arrayWithoutNull([
            {
                label: "Neue Rechnung erstellen",
                command: this.newInvoice.bind(this)
            },
            {
                label: "Neue Rechnung aus Vorlage erstellen",
                items: this.templates.map(template => ({
                    label: template.doc.templateName,
                    command: () => this.newCopiedInvoice(template.doc.id, "copy")
                }))
            },
            doc != null && doc.doc.status == "Abgeschlossen" ? {
                label: "Storno-Gutschrift zu dieser Rechnung erstellen",
                command: () => this.newCopiedInvoice(doc.doc.id, "creditNote")
            } : null,
            doc != null ? {
                label: "Diese Rechnung kopieren",
                command: () => this.newCopiedInvoice(doc.doc.id, "copy")
            } : null
        ]);
    }

    
    //TODO: Das gehört eigentlich in Klasse GenericDataType
    toTableCellType(type: GenericDataType): TableCellType {
        switch (type) {
            case GenericDataType.string:
                return TableCellType.text;
            case GenericDataType.int:
                return TableCellType.int;
            case GenericDataType.decimal:
                return TableCellType.decimal;
            case GenericDataType.bool:
                return TableCellType.yesNo;
            case GenericDataType.date:
                return TableCellType.date;
            case GenericDataType.dateTime:
                return TableCellType.dateTime;
            default:
                return TableCellType.text;
        }
    }

    locationFilterChanged(): void {
        localStorage.setItem("selectedLocations", Utils.toJson(this.selectedLocations));
        this.refillTable();
    }

    refillTable(): void {
        let docs = this.docs.filter(doc => !this.showOnlyOwn || Utils.stringEqualsCi(doc.doc.createdBy, this.app.getUserName()));

        //PRAGMATIC
        docs = docs.filter(doc => this.selectedLocations.length == 0 || this.selectedLocations.includes(doc.doc.getField("biller")?.getValue(null)));

        this.table = this.createTableData(docs);
    }

    createTableData(docs: GenDocDto[], withMetaInfo = true) {
        return new TableData([
            withMetaInfo ? new TableColumn("status", "Status", TableCellType.text, { autoWidth: 10 }) : null,
            ...this.form.tableColumns.map(col => new TableColumn(col.id, col.title, this.toTableCellType(col.dataType), { flex: 1 })),
            withMetaInfo ? new TableColumn("modifiedBy", "Geändert von", TableCellType.text, { autoWidth: 15 }) : null,
            withMetaInfo ? new TableColumn("modifyDate", "Änderungsdatum", TableCellType.dateTime, { autoWidth: 1 }) : null,
            withMetaInfo ? new TableColumn("createdBy", "Erstellt von", TableCellType.text, { autoWidth: 15 }) : null,          
            withMetaInfo ? new TableColumn("creationDate", "Erstelldatum", TableCellType.dateTime, { autoWidth: 1 }) : null    
        ], docs.map(doc => new TableRow(Utils.toString(doc.doc.id), doc, {
            status: doc.doc.status,
            ...Utils.arrayConvertToOrdinaryObject(this.form.tableColumns, col => col.id, col => doc.tableColumns[col.id]),
            modifyDate: doc.doc.modifyDate,
            modifiedBy: doc.doc.modifiedBy,
            creationDate: doc.doc.creationDate,
            createdBy: doc.doc.createdBy
        })), []);
    }

    async newInvoice(): Promise<void> {
        await this.app.navigateTo(["/invoice-generator", "edit-invoice"]);
    }

    async newCopiedInvoice(id: number, copyType: string): Promise<void> {
        await this.app.navigateTo(["/invoice-generator", "edit-invoice", id, copyType]);
    }

    async openDocument(row: TableRow): Promise<void> {
        await this.app.navigateTo(["/invoice-generator", "edit-invoice", (row.raw as GenDocDto).doc.id]);
    }
}
